import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { IconButton } from './IconButton'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}

const sizeMap = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const handleClose = () => onClose()
    dialog.addEventListener('close', handleClose)
    return () => dialog.removeEventListener('close', handleClose)
  }, [onClose])

  return (
    <dialog
      ref={dialogRef}
      className={`w-[calc(100%-2rem)] ${sizeMap[size]} max-h-[calc(100dvh-2rem)] p-0 bg-white rounded-xl shadow-soft-lg backdrop:bg-black/50`}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose()
      }}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between px-5 py-4 @sm:px-7 @sm:py-5 border-b border-black/[0.05]">
          <h2 className="font-sans font-540 text-subhead tracking-subhead">{title}</h2>
          <IconButton aria-label="Close" onClick={onClose} variant="glass-dark">
            <span aria-hidden>×</span>
          </IconButton>
        </div>
        <div className="min-h-0 overflow-y-auto px-5 py-5 @sm:px-7 @sm:py-6">{children}</div>
        {footer && (
          <div className="shrink-0 px-5 py-4 @sm:px-7 border-t border-black/[0.05] flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  )
}
